// Per-remote-peer arriving loudness registry (dBFS).
//
// LiveKitBackgroundAudio measures each remote peer's TRUE arriving loudness on
// its source node every ~200 ms (the same value it prints as `[loudness]`).
// It stashes the latest reading here, keyed by the LiveKit identity (= DB user
// id). The voice-diag collector (voiceTelemetry.ts) reads it when it builds the
// per-peer `inbound` array, so the loudness lands in ClickHouse
// (`voice_diag_samples.inbound[].loudnessDbfs`) instead of only the console.
//
// Why a tiny shared module and not a store: this is hot (every 200 ms × peers),
// purely diagnostic, and must not trigger React re-renders. A plain Map is the
// cheapest correct thing. Listener-side by design — one upgraded client
// captures EVERY peer's arriving loudness regardless of the sender's build.

interface Reading {
  dbfs: number
  /** performance.now() of the last update — lets readers ignore stale entries. */
  at: number
}

const readings = new Map<string, Reading>()

/** Called by LiveKitBackgroundAudio's measurement loop each tick (speech only). */
export function setPeerLoudness(identity: string, dbfs: number): void {
  if (!identity) return
  readings.set(identity, { dbfs, at: Date.now() })
}

/**
 * Latest arriving loudness for a peer, or null if never measured / too stale.
 * Stale = older than `maxAgeMs` (default 4 s) → the peer has been silent, so a
 * cached loud reading would be misleading in telemetry.
 */
export function getPeerLoudness(identity: string, maxAgeMs = 4000): number | null {
  const r = readings.get(identity)
  if (!r) return null
  if (Date.now() - r.at > maxAgeMs) return null
  return r.dbfs
}

/** Drop a peer when its audio graph is torn down (leave / track end). */
export function clearPeerLoudness(identity: string): void {
  if (identity) readings.delete(identity)
}
